import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BiSearch, BiEditAlt } from "react-icons/bi";
import { toast } from "react-toastify";

import { getJrList, reviseJrActivity, jrV2Selector } from "../job-request/jrV2Slice";
import { Colors } from "../../../assets/Colors";

// Halaman ini menampilkan JR V2 yang activity-nya perlu diisi: 100 (Menunggu
// Activity), 110 (Revisi JR setelah recall), dan 201 (Partial Reject — item
// ditolak client). JR yang ditolak di approval internal sudah otomatis kembali
// ke 100/110. Untuk 201, "Revisi Activity" membuka kembali item yang ditolak
// (reviseJrActivity → status 100/110) lalu membuka halaman isi activity.
const STATUS_FILTER = "100,110,201";
const OperationalOutputPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jrV2 = useSelector(jrV2Selector);

  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    dispatch(getJrList({ Status: STATUS_FILTER }));
  }, []); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => {
      dispatch(getJrList({ search: inputValue, Status: STATUS_FILTER }));
    }, 400);
    return () => clearTimeout(t);
  }, [inputValue]); // eslint-disable-line

  const list = jrV2.list ?? [];

  const [busyID, setBusyID] = useState(null);

  const goDetail = async (row) => {
    if (row.Status === 201) {
      // Buka kembali item yang ditolak client sebelum diisi ulang.
      setBusyID(row.JrID);
      try {
        await dispatch(reviseJrActivity(row.JrID)).unwrap();
      } catch (e) {
        toast.error(e?.message || "Gagal membuka revisi activity", { position: "bottom-right" });
        setBusyID(null);
        return;
      }
      setBusyID(null);
    }
    navigate("/operasional-activity-detail-v2", { state: { JrID: row.JrID } });
  };

  return (
    <div className="md:ml-64">
      <div style={{ background: Colors.primary }} className="pt-14 pb-20 px-8" />

      <div className="px-4 md:px-8 -mt-16">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-bold text-gray-800">Activity — Menunggu Activity / Revisi JR / Ditolak Client</h2>
          </div>

          {/* Search */}
          <div className="px-6 py-3 border-b bg-gray-50">
            <div className="relative max-w-sm">
              <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input type="text" value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder="Cari JrID, no. QO, nama klien, atau CQ…"
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg outline-none" />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {jrV2.isLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Memuat data…</div>
            ) : list.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <span className="text-4xl mb-3">🛠️</span>
                <p className="text-sm">Belum ada JR yang menunggu activity atau perlu direvisi</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-5 py-3 font-semibold text-gray-600">No. JR</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">No. QO</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">Nama Klien</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">CQ</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">Tgl Kerja</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">SPK No</th>
                    <th className="px-5 py-3 font-semibold text-gray-600 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {list.map(row => (
                    <tr key={row.JrID} className="hover:bg-indigo-50">
                      <td className="px-5 py-3.5 font-mono text-xs text-gray-700 whitespace-nowrap">
                        {row.JrID}
                        {row.Status === 110 && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-sans font-semibold bg-amber-100 text-amber-700">
                            Revisi · Rev {row.Revision}
                          </span>
                        )}
                        {row.Status === 201 && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-sans font-semibold bg-red-100 text-red-700">
                            Ditolak Client
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 font-mono text-xs text-gray-600">{row.QoNo}</td>
                      <td className="px-5 py-3.5 text-gray-700 max-w-[180px] truncate">{row.NamaKlien || row.KlienID}</td>
                      <td className="px-5 py-3.5 text-gray-700 max-w-[200px]">
                        {row.KodeCQ ? (
                          <div className="truncate" title={row.NamaCQ ? `${row.KodeCQ} — ${row.NamaCQ}` : row.KodeCQ}>
                            <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700">{row.KodeCQ}</span>
                            {row.NamaCQ && <span className="ml-1.5 text-gray-600">{row.NamaCQ}</span>}
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-5 py-3.5 text-gray-700 whitespace-nowrap">{row.WorkDate}</td>
                      <td className="px-5 py-3.5 text-gray-600">{row.SPKNo || <span className="text-gray-400">—</span>}</td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={() => goDetail(row)} disabled={busyID === row.JrID}
                          title={row.Status === 201 ? "Buka kembali item yang ditolak client, lalu isi ulang activity" : undefined}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-medium disabled:opacity-50"
                          style={{ background: row.Status === 201 ? "#dc2626" : Colors.primary }}>
                          <BiEditAlt className="text-sm" />
                          {busyID === row.JrID ? "Membuka…" : row.Status === 201 ? "Revisi Activity" : "Isi Activity"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {!jrV2.isLoading && list.length > 0 && (
            <div className="px-6 py-3 border-t bg-gray-50 text-xs text-gray-500">
              Menampilkan <strong>{list.length}</strong> JR menunggu activity / revisi
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperationalOutputPage;
